import type {FileViewForm} from 'typings/file-view-form';
import type {Restriction} from '../search/criteria.types';
import type {Kind, SearchType, State} from '../commons/constants/portaro.constants';
import type {AnyObject} from 'typings/portaro.fe.types';
import type {ValueEditorBindings} from '../commons/value-editors/kp-value-editor/types';
import type {KpUniversalFormSettings} from '../commons/value-editors/kp-universal-form/types';

export type UUID = string;

export interface LabeledIdentified<ID> extends Identified<ID>, Labeled {
}

export interface Identified<ID> {
    id: ID;
}

export interface NamedIdentified<ID> extends Identified<ID> {
    name: string;
}

export interface Labeled {
    text: string;
}

export interface NamedLabeledIdentified<ID> extends LabeledIdentified<ID> {
    name: string;
}

export interface BasicNamedLabeledIdentified<ID> {
    name: string;
    id: ID;
    text: string;
}

export interface Field extends NamedLabeledIdentified<string>, FieldList {
    idPath: string;
    empty: boolean;
    code: string;
    repetition: number;
    kind: string;
    type: FieldType;
    value: string | Identified<string>; // TODO maybe add generic argument form value
    raw: string;
    fields: Field[];
    url: boolean;
    editor: CustomValueEditorBindings;
    isForAuthority: boolean;
    ind1?: Indicator;
    ind2?: Indicator;
}

export interface EditableField extends Field {
    addableFieldTypes?: [];
    primaryField?: Field;
}

export interface Indicator extends BasicNamedLabeledIdentified<string> {
    indicatorNumber: number;
    default: boolean;
}

// @ts-ignore overwrite string literals type with string
export interface CustomValueEditorBindings extends ValueEditorBindings {
    type: string;
}

export interface FieldType extends LabeledIdentified<string> {
    code: string;
    repeatable: boolean;
}

export interface ExtendedDocument extends RichDocument {
    getFields: (fieldNumbers: string | string[]) => Field[];
    getSubfields: (fieldNumbers: string | string[], codes?: string) => Field[];
}

export interface Isbn {
    value: string;
    trimmedValue: string;
    valid: boolean;
}

export interface RichDocument extends Document, RichRecord {
    status: DocumentStatus;
    subTitle: string;
    author: string;
    isbn: Isbn;
    publisher: string;
    publicationYear: number;
    toc: string;
    periodical: boolean;
    exemplarable: boolean;
    downloadable: boolean;
    volumeable: boolean;
    withForeignOccurrences: boolean;
}

export interface DocumentStatus extends RecordStatus {
    forDeleted: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface Document extends Record {
}

export interface Fond {
    id: number;
    name: string;
    order: number;
    text: string;
    webStyleId: number;
    editable: boolean;
    enabled: boolean;
    ofDocument: boolean;
    forSourceDocument: boolean;
    z39Typ: number;
    periodical: boolean;
    documentCategories: string;
    documentTypes: string;
    withExemplars: boolean;
    recordType: string;
    bibliographicLevel: string;
}

export interface DocumentRating {
    id: number;
    recordId: string;
    count: number;
    value: number;
    lastRatingDate: string;
}

export interface IdentifiedFile extends Resource, LabeledIdentified<number> {
}

export interface ViewableFile extends IdentifiedFile {
    exportable: boolean;
    printable: boolean;
}

export interface Resource {
    hasTextualForm: boolean;
    name: string;
    directory: Directory;
    viewForms: FileViewForm[];
    category: FileCategory;
    accessType: FileAccessType;
    filename: string;
    size: number;
    source: string;
    creatorId: number;
    url: string;
    creationDate: string;
}

export interface Directory extends NamedLabeledIdentified<number> {
    parentDirectoryId: number;
    order: number;
    accessType: FileAccessType;
}

export interface FileCategory {
    id: number;
    name: string;
    acceptableFileTypes: string[];
    text: string;
}

export interface FileAccessType {
    text: string;
    id: number;
}

export interface Valuable<T> {
    value: T;
}

export interface ListResponse<T> {
    totalElements: number;
    content: T[];
}

export interface Loan extends Identified<string> {
    lendDate: string;
    chunkable: boolean;
    department: Department;
    designation: string;
    document: Document;
    dueDate?: string;
    endDate: string;
    estimatedAvailableDate?: string;
    exemplar: Exemplar;
    expiredLoan: boolean;
    inQueue: boolean;
    lastRenewalDate?: string;
    loanCategory: LoanCategory;
    loanPeriod?: number;
    loanRealizationId: number;
    loanRequestId: number;
    loanType: LoanType;
    location: Location;
    orderDate: string;
    overdueNoticesCount: number;
    queueNumber: number;
    queueOrder: number;
    requestCancelDate: string;
    requestDate: string;
    reservationDate: string;
    reservationDeadlineDate?: string;
    state: LabeledIdentified<string> & {finished: boolean};
    stateText: Text;
    text: Text;
    user: BasicUser;
    desiredDepartments: Department[];
}

export type EnumToLabelledIdentified<T> = {id: keyof T, text: string};

export enum LoanState {
    // Pozadavek na rezervaci (neboli nepotvrzena rezervace)
    ACTIVE_NOT_APPROVED_REQUEST = 'ACTIVE_NOT_APPROVED_REQUEST',

    // Zruseno ve fazi pozadavku
    CANCELLED_NOT_APPROVED_REQUEST = 'CANCELLED_NOT_APPROVED_REQUEST',

    // Rezervace v poradi
    ON_WAITING_LIST = 'ON_WAITING_LIST',

    // Neodeslaná rezervace
    UNSENT_RESERVATION = 'UNSENT_RESERVATION',

    // Odeslana rezervace
    SENT_RESERVATION = 'SENT_RESERVATION',

    // Nevyrizena objednavka
    UNPROCESSED_ORDER = 'UNPROCESSED_ORDER',

    // Vyrizena objednavka
    PROCESSED_ORDER = 'PROCESSED_ORDER',

    // Vypujcka
    LENT = 'LENT',

    // Vracena vypujcka
    RETURNED = 'RETURNED',

    // Zrusena rezervace
    CANCELLED_RESERVATION = 'CANCELLED_RESERVATION',

    // Neznamy stav
    UNKNOWN = 'UNKNOWN'
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface LoanType extends LabeledIdentified<number> {
}

export interface Message extends Identified<UUID> {
    topic: EnumToLabelledIdentified<MessageTopic>;
    severity: EnumToLabelledIdentified<MessageSeverity>;
    senderUser: BasicUser;
    targetUser: BasicUser;
    creationEvent: Event;
    confirmationNecessary: boolean;
}

export enum MessageTopic {
    COMMON = 0,
    LOAN_COMMON = 1000,
    REMINDER = 1100,
    ORDER = 1200,
    PRE_REMINDER = 1300,
    USER_COMMON = 2000,
    USER_REGISTRATION = 2100,
    REGISTRATION_CREATION = 2110,
    MESSAGE_FROM_READER = 3000,
    LIBRARY_NOTIFICATION = 5000,
    ADVERTISEMENT_COMMON = 6000,
    ERROR_COMMON = 8000
}

export enum MessageSeverity {
    INFO = 'info',
    WARNING = 'warning',
    URGENT = 'urgent'
}

export enum MessageMedium {
    UNDEFINED = 'undefined',
    INTERNAL = 'internal',
    POST = 'post',
    SMS = 'sms',
    EMAIL = 'email'
}

export enum MessageStatus {
    SENT = 'sent',
    UNSENT = 'unsent'
}

export interface MessageSending extends Identified<UUID> {
    message: Message;
    recipientUser: BasicUser;
    messageMedium: EnumToLabelledIdentified<MessageMedium>;
    sendingEvent?: Event;
    receiptionEvent?: Event;
    confirmationEvent?: Event;
}

export interface MessageSendingEmail extends MessageSending {
    recipientEmailAddress: string;
    senderEmailAddress: string;
    subject: string;
    body: string;
    attachmentDirectoryId?: number;
    errorMessage?: string;
}

export interface MessageSendingPost extends MessageSending {
    address: string;
    fulltextId?: number;
    printingEventId?: Event;
}

export interface MessageSendingSms extends MessageSending {
    recipientPhoneNumber: string;
    content: string;
    errorMessage?: string;
}

export interface MessageSendingInternal extends MessageSending {
    subject: string;
    body: string;
}

export interface Event extends Identified<UUID> {
    id: UUID;
    code: string;
    createDate: string;
    sessionId?: string;
    initiatorUser?: BasicUser;
    department?: Department;
    data?: string;
}

export interface BasicUser extends LabeledIdentified<number> {
    evided: boolean;
    printableName: string;
}

export interface User extends BasicUser {
    kind: UserKind;
    role: string[];
    userRoles: UserRole[];
    username: string;
    address: UserAddress[];
    contacts: Contact[];
    representedUsers: BasicUser[];
    authority: NamedLabeledIdentified<number>;
    readerRole: any;
    editorAccounts: any[];
    externalUserRole: any;
    email: string;
    registrationExpired: boolean;
    readerBuildings: Department[];
    hasServicePrivileges: boolean;
    readableDepartments: Department[];
}

export interface Person extends User {
    firstName: string;
    lastName: string;
    degree: string;
    suffixDegree: string;
    openidId: string;
    guId: string;
    netId: string;
    birthDate: string;
    job: string;
    jobAddress: string;
    educationLevel: string;
    schoolClass: string;
    identityCardNumber: string;
    mojeIdLastUpdateDate: string;
    mojeIdValid: number;
    bakalari: string;
    edupageId: string;
    edookitId: string;
    solId: string;
}

export interface Auth {
    role: string[];
    activeUser: User;
    evided: boolean;
    editableDepartments: Department[];
}

export interface UserRole {
    text: string;
    roleName: string;
}

export enum DepartmentEditationMode {
    OVERWRITE_ALL = 'OVERWRITE_ALL' ,
    MERGE_DUPLICATES_OR_APPEND = 'MERGE_DUPLICATES_OR_APPEND'
}

export interface UserEditationRequest extends Identified<number> {
    kind: UserKind;
    active: boolean;
    authority?: NamedLabeledIdentified<number>;
    readerAccounts?: ReaderAccountEditationRequest[];
    editorAccounts?: EditorAccountEditationRequest[];
    supplierAccounts?: SupplierAccountEditationRequest[];
    emails?: UserEmailEditationRequest[];
    phoneNumbers?: PhoneNumberEditationRequest[];
    addresses?: UserAddressEditationRequest[];
    username?: string;
    newPassword?: string;
    readableDepartments?: Department[];
    readableDepartmentsEditationMode?: DepartmentEditationMode;
    editableDepartments?: Department[];
    editableDepartmentsEditationMode?: DepartmentEditationMode;
    editableFonds?: Fond[];
}

export interface ReaderAccountEditationRequest {
    deleted?: boolean;
    blocked?: boolean;
    registrationDate?: string;
    registrationExpirationDate?: string;
    readerCategory?: ReaderCategory;
    originDepartment?: Department;
    reservationsPrintType?: PrintType;
    overdueNoticesPrintType?: PrintType;
    cardNumber?: string;
    barCode?: string;
    note?: string;
    librarianMessage?: string;
    rfid?: string;
}

export interface EditorAccountEditationRequest {
    withServicePrivileges?: boolean;
    validationCode?: string;
    group?: LibrarianGroup;
    editLevel?: EditLevel;
    active?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface LibrarianGroup extends BasicNamedLabeledIdentified<number> {
}

export interface EditLevel extends Valuable<number>, Labeled {
}

export type SupplierAccountEditationRequest = AnyObject;

export interface UserEmailEditationRequest {
    value?: string;
    source: LabeledIdentified<string>;
}

export interface PhoneNumberEditationRequest {
    value: string;
    smsCapable?: boolean;
    source: LabeledIdentified<string>;
}

export interface UserAddressEditationRequest {
    permanent?: boolean;
    mailing?: boolean;
    source?: string;
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
}

export enum PrintType {
    POST = 0,
    EMAIL = 1,
    SMS = 2,
    EMAIL_AND_SMS = 3
}

export interface ReaderCategory {
    registrationFee: number;
    registrationDurationMonths: number;
    lendingAllowed: boolean;
    remindingActive: boolean;
    loanCountLimit?: number;
    registrationToDateInFormatDDMM?: string;
    internetSessionLimitMinutes?: number;
    groupable: boolean;
    reservationsCountLimit: number;
    ordersCountLimit: number;
}

export type RecordKind = Kind.KIND_AUTHORITY | Kind.KIND_DOCUMENT;

export enum UserKind {
    USER = 'USER',
    PERSON = 'PERSON',
    LIBRARY = 'LIBRARY',
    INSTITUTION = 'INSTITUTION',
    FAMILY = 'FAMILY',
    SOFTWARE = 'SOFTWARE'
}

export interface UserAddress extends Identified<number> {
    permanent: boolean;
    mailing: boolean;
    address: Address;
}

export interface Address extends LabeledIdentified<number> {
    street: string;
    city: string;
    postalCode: string;
    country: LabeledIdentified<string>;
}

export interface Contact extends Identified<number> {
    userId: number;
    type: {
        id: ContactType,
        text: string;
    };
    value: string;
}

export enum ContactType {
    SMS_PHONE = 0,
    PHONE = 1,
    EMAIL = 2
}

export interface Department extends NamedLabeledIdentified<number> {
    order: number;
    root: boolean;
    exemplarable: boolean;
    parentId: number;
    sigla: string;
    online: boolean;
    central: boolean;
}

export interface Location extends NamedLabeledIdentified<number>, Ordered {
    code: string;
    departments: Department[];
    editable: boolean;
    deletable: boolean;
}

export interface UserDetailManagerDto {
    renewAllShowing: boolean;
    moneyShowing: boolean;
    loansShowing: boolean;
    mvsShowing: boolean;
    userEditationShowing: boolean;
    enabledPaymentProviderNames: string[];
    canReturnAny: boolean;
    canCancelAny: boolean;
    showUserReceiptOfPaymentPrintButton: boolean;
    showUserRegistrationAgreementPrintButton: boolean;
    showLibrarianMessage: boolean;
}

export interface ListResult<ITEM> {
    totalElements: number;
    content: ITEM[];
}

export interface ActionResponse {
    responseType: string;
    finished: boolean;
}

export interface ActionResponseWithText extends ActionResponse {
    text: string;
}

// eslint-disable-next-line
export interface FinishedActionResponse extends ActionResponseWithText {
}

export interface FinishedSaveResponse<T> extends ActionResponseWithText {
    savedObject: T;
}

export interface ActionResponseWithNestedResponses extends ActionResponse {
    finishedResponses: ActionResponse[];
}

export interface ExceptionActionResponse extends ActionResponse {
    simpleName: string;
}

export interface FormPackage<E> extends ActionResponseWithText {
    formSetting: KpUniversalFormSettings<E>;
    formObject: E;
}

export interface FieldEnablingExceptionContinuation extends ExceptionActionResponse {
    continuation: {
        fieldToEnable: string;
        confirmationText: string;
    }
}

export interface SettingTypeIdContainingException extends ExceptionActionResponse {
    text: string;
    settingTypeId: string;
}

export interface UserPreferencesException extends ExceptionActionResponse {
    text: string;
    userPreferenceKeys: number[];
}

export interface PayloadAwareException<PAYLOAD> {
    payload: PAYLOAD;
}

export interface ActionResponseWithMessage extends ActionResponse {
    message: string;
}

export interface RedirectionResponse extends ActionResponseWithText {
    url: string;
    strategy: string;
}

export interface LoanRequestFormActionResponse extends ActionResponse {
    responseType: 'LoanRequestForm';
    finished: false;
    form: LoanRequestByDocumentsGroupsPane;
}

export interface LoanRequestByDocumentsGroupsPane {
    groups: LoanRequestSameOptionsGroupPane[];
}

export interface LoanRequestSameOptionsGroupPane {
    requestItems: LoanRequestItem[];
    capabilities: Capability[];
    options: LoanRequestOption[];
}

export interface LoanRequestOption {
    capability: Capability;
    formObject: AnyObject;
    form: KpUniversalFormSettings<AnyObject>;
}

export interface FinishedReturningActionResponse extends FinishedActionResponse {
    responseType: 'FinishedReturningResponse';
    loan: Loan;
}

export interface FinishedLendingActionResponse extends FinishedActionResponse {
    responseType: 'FinishedLendingResponse';
    lender: User;
    documentName: string;
    dueDate: string;
}

export interface Form {
    id: any;
    text: string;
    fields: EditedProperty[];
    header: string;
    footer: string;
}

export interface EditedProperty {
    label: string;
    fieldName: string;
    editor: ValueEditorBindings;
}

export interface DocumentAvailability extends Availability {
    buildingsToOrderOn: Department[];
    buildingsToPickupOn: Department[];
    buildingsToReserveOn: Department[];
    detailed: boolean;
    mailOrderableWhenNoExemplars: boolean;
    returnedToday: boolean;
    freeItemsCount: number;
    freeTakeawayItemsCount: number;
    obtainableItemsCount: number;
    reservationsInQueueCount: number;
    reservationsCount: number;
    freeVisitableItemsCount: number;
    lastUserLoanDate: string;
}

export interface Availability extends Identified<string>, PlainAvailability {
    obtainable: boolean;
    capabilities: Capability[];
}

export interface PlainAvailability {
    known: boolean;
    reasonable: boolean;
    reservable: boolean;
    orderable: boolean;
    pickupable: boolean;
    free: boolean;
}

export interface Capability {
    name: CapabilityName;
    loginRequired: boolean;
}

export type CapabilityName =
    'slotVisitOrderability' |
    'mailRequestability' |
    'standardVisitReservability' |
    'visitPickupability' |
    'standardVisitOrderability' |
    'pickupability' |
    'flexibooksLoanability' |
    'downloadability' |
    'ereadingLoanability' |
    'levnaknihovnaLoanability' |
    'mvsRequestability' |
    'standardReservability' |
    'standardOrderability';

export interface CapabilityOnBuildings extends Capability {
    buildings: Department[];
}

export interface StandardOrderability extends CapabilityOnBuildings {
    name: 'standardOrderability';
}

export interface SlotVisitOrderability extends CapabilityOnBuildings {
    name: 'slotVisitOrderability';
    timeslots: RequestableTimeslot[];
}

export interface RequestableTimeslot extends Identified<string> {
    from: string;
    to: string;
    orderable: boolean;
}

export interface Exemplar extends Identified<number> {
    accessNumber: string;
    account: unknown;
    acquisitionWay: AcquisitionWay;
    acquisitionYear: number;
    attachments: string;
    barCode: string;
    binding: boolean;
    bindingIssueRange: string;
    bundledVolume: boolean;
    bundledVolumeIssueRange: string;
    bundledVolumeNumber: string;
    bundledVolumeYear: string;
    creationDate: string;
    customValue: BasicNamedLabeledIdentified<string>;
    department: Department;
    designation: string;
    designationProperty: string;
    discarded: boolean;
    discardionId: number;
    fileDirectory: Directory;
    firstExistingDesignationProperty: string;
    fond: Fond;
    invoiceItem: number;
    invoiceNumber: string;
    issue: boolean;
    issueEvidenceNumber: string;
    issueName: string;
    lastModificationDate: string;
    loanCategory: LoanCategory;
    location: Location;
    note: string;
    order: number;
    owner: string;
    price: number;
    quantity: number;
    recordId: string;
    requestable: boolean;
    signature: string;
    signatureSorter: string;
    status: ExemplarStatus;
    supplier: string;
    thematicGroup: ThematicGroup;
    type: ExemplarType;
    vazbaId: number;
    volumeId: number;
    volumeNumber: string;
    volume?: Volume;
}

export interface AvailabilityAwareExemplar extends Exemplar {
    availability: Availability;
}

export interface PlacementAwareExemplar extends Exemplar {
    placement: Regal;
}

export interface Dimensions {
    width: number;
    height: number;
}

export interface Regal {
    polygonPointsX: number[];
    polygonPointsY: number[];
    map: Int8Array;
    mapWidth: number;
    mapHeight: number;
    information: string;
    withMap: boolean;
    dimensions?: Dimensions;
}

export interface RecordExport extends BasicNamedLabeledIdentified<string> {
    recordOperationTypes: RecordOperationType[] | null;
    fonds: Fond[] | null;
    withPerio: boolean;
    withNonperio: boolean;
    withNonexemplared: boolean;
    forbiddenRecordIds: UUID[] | null;
    sqlClause: string | null;
}

export interface ExemplarStatus extends BasicNamedLabeledIdentified<number> {
    lendable: boolean;
    reservable: boolean;
    discarded: boolean;
}

export interface Discardion extends Identified<number> {
    exemplar: Exemplar;
    discardNumber: string;
    reason: string;
    replacementWay: ReplacementWay;
    cost: number;
    consultationNumber: string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ReplacementWay extends NamedLabeledIdentified<string> {
}

export interface ThematicGroup extends BasicNamedLabeledIdentified<number> {
    order: number;
}

export interface LoanCategory extends BasicNamedLabeledIdentified<string> {
    lendable: boolean;
    inPlace: boolean;
    renewable: boolean;
    circular: boolean;
    reminderable: boolean;
    reservable: boolean;
    forDislocation: boolean;
    chunkable: boolean;
    returningImmediately: boolean;
}

export interface AcquisitionWay extends NamedLabeledIdentified<string> {
    order: number;
}

export enum ExemplarType {
    EXEMPLAR = 0,
    ISSUE = 1,
    BINDING = 2
}

export interface ViewableExemplar extends Exemplar {
    availability: AppserverDataBackedExemplarAvailability;
    holder: LabeledIdentified<number>;
    holderShowable: boolean;
    holderDetailShowable: boolean;
    editable: boolean;
    discardable: boolean;
    deletable: boolean;
    discardionRestorable: boolean;
}

export interface AppserverDataBackedExemplarAvailability extends AbstractKnownAvailability {
    text: string;
    capabilities: Capability[];
    holderId: number;
    lentToDate: string;
    errorNumber: number;
    errorText: string;
    appserverData: ExemplarAvailabilityAppserverData;
    free: boolean;
    reasonable: boolean;
    lent: boolean;
    error: boolean;
}

export interface AbstractKnownAvailability {
    id: any;
    known: boolean;
    obtainable: boolean;
    reservable: boolean;
    orderable: boolean;
    pickupable: boolean;
    mailOrderableWhenNoExemplars: boolean;
    pickupLoanRequestPermitted: AbstractKnownAvailabilityPermission;
}

enum AbstractKnownAvailabilityPermission {
    ALLOWED = 'ALLOWED',
    MAYBE_AFTER_LOGIN = 'MAYBE_AFTER_LOGIN',
    AFTER_UPGRADE = 'AFTER_UPGRADE',
    FORBIDDEN = 'FORBIDDEN'
}

export interface ExemplarAvailabilityAppserverData {
    error: boolean;
    errors: AppserverError[];
    lentToDate: string;
    holderId: number;
}

export interface AppserverError {
    error: boolean;
    errorNumber: number;
    errorMessage: string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface Inventory extends BasicNamedLabeledIdentified<number> {
}

export interface Capture extends Identified<number> {
    inventory: Inventory;
    identifier: string;
    exemplar: number;
    quantity: number;
    captureWay: number;
}

export interface RecordEditation extends Identified<string>, FieldList {
    authority: boolean;
    draft: boolean;
    deleted: boolean;
    fond: Fond;
    text: string;
    revisionSaved: boolean;
    fondSelectionEditor: ValueEditorBindings;
    showIndicators: boolean;
    showFieldIdentifiers: boolean;
    directoryId: any;
    recordId: string;
}

export interface EditableFieldType extends FieldType {
    displayType: FieldDisplayType;
    editNote: string;
    entryElement: boolean;
    subfieldTypes: EditableFieldType[];
    targetAuthorityLinkedSubfieldType?: any;
    editor: ValueEditorBindings;
    indicator1Type?: ViewableIndicatorType;
    indicator2Type?: ViewableIndicatorType;
}

export interface ViewableIndicatorType extends Identified<any>{
    editor: ValueEditorBindings;
}

export interface FieldDisplayType extends LabeledIdentified<number>, Identified<number> {
}

export interface Record extends NamedLabeledIdentified<string> {
    type: RecordKind;
    kindedId: number;
    detail: FieldList;
    fond: Fond;
    external: boolean;
    status: RecordStatus;
    fields: Field[];
}

export interface RichRecord extends Record {
    directoryId: number;
    cover: IdentifiedFile;
    lastUpdateDate: string;
}

export interface RecordStatus extends LabeledIdentified<number> {
    forActiveRecord: boolean;
}

export interface RecordHolding {
    recordId: string;
    department: Department;
}

export interface RecordSearchParams extends StaticSearchParams {
    kind: RecordKind;
    focusedFieldTypeId?: string;
}

export interface RecordCreateParams {
    kind: RecordKind;
    fond?: Fond;
    focusedFieldTypeId?: string;
}

export interface FieldList {
    fields: Field[];
}

export interface RecordOperation extends Identified<any> {
    user: BasicUser;
    recordOperationType: RecordOperationType;
    date: string;
    record: Record;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface RecordOperationType extends BasicNamedLabeledIdentified<number> {
}

export interface ForeignOccurrence {
    databaseId: number;
    databaseName: string;
    itemUrl: string;
    itemId: string;
}

export interface SearchHistoryEntryDto {
    date: string;
    title: string;
    url: string;
    totalElements: number;
    qt: Restriction;
}

export enum SenderDescriptorType {
    CUSTOM = 'CUSTOM',
    SYSTEM = 'SYSTEM'
}

export interface RawBodyMailSendRequest {
    senderType: SenderDescriptorType | keyof typeof SenderDescriptorType;
    recipientEmails?: string[];
    subject?: string;
    body?: string;
    attachments: IdentifiedFile[];
}

export interface SmsSendRequest {
    recipients: User[];
    body: string;
}

export interface Ordered {
    order: number;
}

export interface OrderedNamedIdentified<ID> extends NamedIdentified<ID>, Ordered {

}

export interface ValueEditorProvider {
    editor: ValueEditorBindings;
}

export interface ViewableSearchField extends LabeledIdentified<string>, ValueEditorProvider {
}

export interface Facet extends LabeledIdentified<string>, ValueEditorProvider {
}

export interface Comment extends Identified<string> {
    recordId: string;
    creator: number;
    date: string;
    content: string;
}

export interface CommentCreationRequest {
    creator: number;
    record: string;
    content: string;
}

export interface Rating extends Identified<number> {
    id: number;
    recordId: string;
    count: number;
    value: number;
    lastRatingDate: Date;
    formattedValue: string;
}

export interface Language {
    id: string;
    text: string;
}

export interface Category {
    id: string;
    text: string;
}

export interface Localization {
    code: string;
    depId: number;
    translations: {[key: string]: any};
}

export interface Translation {
    code: string;
    depId: number;
    language: string;
    value: string;
}

export interface Volume extends LabeledIdentified<number> {
    documentId: number;
    recordId: string;
    volumeNumber: string;
    issueQuantity: number;
    year: string;
    date: string;
    description: string;
    periodicity: VolumePeriodicity;
    periodicityMultiplier: number;
    withIssues: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface VolumePeriodicity extends LabeledIdentified<number> {
}

export interface SequenceItem extends Identified<number>, Valuable<string> {
}

export interface UserPreference {
    id: string;
    customized: boolean;
    key: any;
    keyId: number;
    keyText: string;
    user: BasicUser;
    effectiveValue: any;
    editor: ValueEditorBindings;
    defaultValue: any;
    datatype: Datatype;
    customValue: any;
    state?: State;
    editedValue?: any;
}

export interface PassiveMvsRequest extends MvsRequest {
    executor: BasicUser;
    abroadDeliverable: boolean;
    orderDate: string
}

export interface MvsRequest {
    document: RichDocument;
    exemplar: Exemplar;
    article: string;
    createDepartment: Department;
    requester: BasicUser;
    note: string;
    volume: string;
    page: string;
    deadline: string;
    state: BasicNamedLabeledIdentified<number>;
    fillingDate: string;
    acceptanceDate: string;
    executionDate: string;
    sendBackDate: string;
    price: number;
    byCopySolvable: boolean;
    eventuallyReservable: boolean;
    inPlaceLendable: boolean;
}

export interface Transaction extends LabeledIdentified<number> {
    cashier: BasicUser;
    date: string;
    owner: BasicUser;
    sum: number;
    type: AmountType;
    typeId: number;
    payment: boolean;
    paymentId: number;
    debt: boolean;
}

export interface AmountType extends NamedLabeledIdentified<number>, Ordered {
    category: AmountTypeCategory;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface AmountTypeCategory extends LabeledIdentified<number> {
}

export interface AutocompleteValueEditorStaticParams {
    remoteDataSourceUrl: string;
}

export interface ViewableSettingChain<VALUE = any> {
    id: string;
    defaultValue: VALUE;
    name: string;
    section: string;
    description: string;
    datatype: Datatype;
    type: SettingTypeDto<VALUE>;
    chain: ViewableSettingValue<VALUE>[];
    effectiveSettingValue: VALUE;
    editor: ValueEditorBindings;
}

export interface SettingTypeDto<VALUE = any> {
    description: string;
    defaultValue: VALUE;
    datatype: Datatype;
}

export interface ViewableSettingValue<VALUE = any> extends Identified<string> {
    value: VALUE;
    note: string;
    department: LabeledIdentified<number>;
    fond: LabeledIdentified<number>;
    default: boolean;
    editable: boolean;
    overridable: boolean;
    effective: boolean;
}

export interface TransferableSettingsValue<VALUE = any> {
    section: string;
    name: string;
    departmentId: number;
    fondId: number;
    value: VALUE;
    note: string;
}

export interface Datatype {
    name: string;
    structure?: Structure;
    nestedDatatype?: Datatype;
}

enum Structure {
    SCALAR = 'SCALAR',
    LIST = 'LIST'
}

export interface PortaroState {
    runningDays: number;
    version: string;
    versionDetail: string;
    coverSearchState: AnyObject[][];
    maxMemory: number;
    totalMemory: number;
    usedMemory: number;
    procesorsCount: number;
    operatingSystem: string;
    operatingSystemArchitecture: string;
    operatingSystemVersion: string;
    operatingSystemUsername: string;
    javaHome: string;
    javaVersion: string;
    processId: number;
    appserverUrl: string;
    appserverVersion: string;
    databaseDriverClassName: string;
    databaseUrl: string;
    startTime: string;
    licence: Licence;
}

export interface Licence {
    rootUrl: string;
    testing: boolean;
    editorLimit: number;
    documentLimit: number;
    licenceVersion: Valuable<string>
    maintenanceExpirationDate: string;
    enabledModules: string[];
}

export interface ClientSession {
    sessionId: string;
    creationDate: string;
    ipAdress: IpAddress;
    userAgent: string;
    requestEvents: LoginEvent[];
    currentDepartment: Department;
    endDate: string;
    internalAccess: boolean;
    bot: boolean;
}

export interface IpAddress {
    value: string;
    loopback: boolean;
}

export interface LoginEvent {
    user: User;
    date: string;
}

export interface HierarchyTreeNode<ID> extends LabeledIdentified<ID> {
    level: number; // level numbering starts at 1
    current: boolean;
}

export interface HierarchyTree<ID, NODE extends HierarchyTreeNode<ID>> {
    nodes: NODE[];
}

export interface RecordHierarchyTreeNode extends HierarchyTreeNode<UUID> {
    type: string;
    see: boolean;
    seeAlso: boolean;
    parent: boolean;
    child: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface RecordCollectionHierarchyTreeNode extends HierarchyTreeNode<UUID> {
}


// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface RecordDescriptor extends LabeledIdentified<number> {
}

export interface LoanRequest {
    items: LoanRequestItem[];
    name: string;
}

export type LoanRequestItem = ({
    document: RichDocument;
    exemplar?: Exemplar;
} | {
    document?: RichDocument;
    exemplar: Exemplar | number;
}) & {
    fromDate?: string,
    toDate?: string;
};

export interface DocumentCapabilitySlotsRequest {
    document: number;
    requester?: number;
    requestDepartment?: number;
    scopeDateRange?: DateRange;
}

export interface DateRange {
    fromDate?: string,
    toDate?: string
}

export interface SearchParams {
    kind?: Kind;
    type?: SearchType;
    pageNumber?: number;
    pageSize?: number;
    q?: string;
    department?: any[]
    fond?: Fond[];
    sorting?: Sorting;
}

export interface StaticSearchParams extends SearchParams {
    kind: Kind;
    type: SearchType;
}

export interface RemoteValidationAdditionalParameters {
    validationPathUrl: string;
}

export interface RemoteValidationDto<MODEL> {
    fieldName: string;
    value: MODEL;
    formModel?: AnyObject;
    staticParams?: AnyObject;
}

export interface Validity {
    property: string;
    type: string;
    valid: boolean;
    message: string;
}

export interface ViewableSearch<PARAMS extends SearchParams, ITEM> extends Identified<UUID> {
    result: PagedParameteredResult<ITEM, PARAMS>;
    title?: string;
    subtitle?: string;
    type: string;
    availableSortings: Sorting[];
    forms: Form[];
    availableExports: Link[];
    availableIntents: Intent[];
    defaultExpandedFacets: string[];
    features: string[];
}

export interface Intent {
    type: string;
}

export interface GoToIntent extends Intent {
    link: Link;
}

export interface ModifySearchParamsIntent extends Intent {
    newParams: SearchParams;
}

export interface Link {
    url: string;
    text: string;
    note?: string;
}

export interface Sorting extends LabeledIdentified<string>{
    field: string;
    asc: boolean;
}

export interface Pageable {
    pageSize: number;
    pageNumber: number;
}

export interface PagedParameteredResult<ITEM, PARAMS extends SearchParams> extends ListResult<ITEM>, Pageable {
    params: PARAMS;
    totalPages: number;
    numberOfElements: number;
    duration: number;
    first: boolean;
    last: boolean;
}

export interface ViewableExemplarWithDocument extends ViewableExemplar {
    document: RichDocument;
}

export interface ConfirmableRequest {
    confirmed?: boolean;
}

export interface ExemplarRequest extends ConfirmableRequest {
    id?: number;
    document: Document;
    volume?: Volume;
    department?: Department;
    location?: Location;
    identifiers?: ExemplarIdentifierRequest[];
    loanCategory?: LoanCategory;
    thematicGroup?: ThematicGroup;
    acquisitionWay?: AcquisitionWay;
    creationDate?: string;
    customValue?: NamedLabeledIdentified<string>;
    status?: ExemplarStatus;
    price?: number;
    owner?: string;
    acquisitionYear?: number;
    note?: string;
    account?: unknown;
    invoiceItem?: number;
    invoiceNumber?: string;
    attachments?: string;
    order?: number;
    supplier?: string;
    type?: ExemplarType;
    quantity?: number;
    bundledVolumeNumber?: string;
    bundledVolumeYear?: string;
    bundledVolumeIssueRange?: string;
    issueName?: string;
    issueEvidenceNumber?: string;
}

export interface ExemplarIdentifierRequest {
    accessNumber?: string;
    signature?: string;
    barCode?: string;
}

export interface VolumeRequest {
    id?: number;
    document: RichDocument;
    volumeNumber: string;
    year: string;
    issueQuantity: number;
    description: string;
    periodicity: VolumePeriodicity;
    periodicityMultiplier: number;
}

export interface Levelable {
    level: string;
}

export interface Logger extends Levelable, Identified<string> {
}

export interface RelationsRequest {
    target: Identified<number>;
    representative?: Identified<number>;
    sources: Identified<number>[];
}

export interface ReportTemplate {
    id: string;
    name: string;
    description: string;
    exportLink: string;
}

export interface ServerFolder {
    id: string;
    name: string;
    reportTemplates: ReportTemplate[];
    folderSubItems: ServerFolder[];
}

export interface PrintParameter {
    id: string;
    name: string;
    helpText: string;
}

export interface Field007DocumentCategory extends Identified<UUID>, Ordered {
    code: string;
    description: string;
}

export interface Field007Code extends Identified<UUID> {
    position: number;
    documentCategoryCode: string;
    code: string;
    description: string;
    isDefaultValue: boolean;
}

export interface Field007Label extends Identified<UUID>, Ordered {
    documentCategoryCode: string;
    description: string;
}

export interface Field007Definitions {
    documentCategories: Field007DocumentCategory[];
    codes: Field007Code[];
    labels: Field007Label[];
}

export interface Field008Code extends Identified<UUID> {
    position: number;
    documentTypeCode: string;
    code: string;
    description: string;
    isDefaultValue: boolean;
}

export interface Field008AcceptableEditorValue {
    code: string;
    description: string;
}

export interface Field008DocumentType extends Identified<number>, Field008AcceptableEditorValue {
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface Field008PublicationStatus extends Field008AcceptableEditorValue {
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface Field008RecordModification extends Field008AcceptableEditorValue {
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface Field008CataloguingSource extends Field008AcceptableEditorValue {
}

export interface Field008Label extends Ordered {
    documentTypeCode: string;
    description: string;
    isDefined: boolean;
}

export interface Field008Definitions {
    documentTypes: Field008DocumentType[];
    codes: Field008Code[];
    labels: Field008Label[];
    countryCodes: LabeledIdentified<string>[];
    languageCodes: LabeledIdentified<string>[];
    publicationStatuses: Field008PublicationStatus[];
    recordModifications: Field008RecordModification[];
    cataloguingSources: Field008CataloguingSource[];
}

export interface LocationRequest extends OrderedNamedIdentified<number>, ConfirmableRequest {
    code: string;
    departments: Department[];
}

export interface SdiRequest extends BasicNamedLabeledIdentified<number> {
    user: BasicUser;
    department: Department;
    query: string;
    periodicity: Periodicity;
    createDate: string;
    terminationDate: string;
    active: boolean;
    deletable: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface Periodicity extends LabeledIdentified<number> {
}

export interface SdiSending extends Identified<number> {
    sdiRequestId: number;
    date: string;
    recordsCount: number;
    error: string;
}

export interface SdiRequestCreationRequest extends NamedIdentified<number> {
    user: BasicUser;
    search: ViewableSearch<RecordSearchParams, RichDocument>;
}

export interface SdiRequestEditRequest extends Identified<number> {
    active: boolean
}

export interface FacetType extends NamedLabeledIdentified<number>, Ordered {
    definition?: string;
    exemplarType: boolean;
    enabled: boolean;
    sorting: Sorting;
    definitionType: LabeledIdentified<number>;
    scope: LabeledIdentified<number>;
    datatype?: string
}

export interface Filter extends NamedIdentified<number> {
    filters?: Filter[];
    matcher?: Matcher;
    matchResult?: MatchResult;
    description?: string;
}

export interface Matcher {
    name?: string;
    description?: string;
    sourceData?: SourceData;
    matchers?: Matcher[];
    target?: Matcher;
}

export interface MatchResult {
    allowed: boolean;
    denied: boolean;
    messageCode: string | null;
}

export interface SourceData extends Identified<number> {
    type: string | null;
    negated: boolean;
    value: string | null;
    description: string | null;
}

export interface RenewalResponse extends ActionResponseWithText {
    numberOfDays: number
    renewalsCount: number
    returnDate;
}

export interface DirectoryInsight {
    withSubdirectories: boolean;
    withNonCoverFiles: boolean;
    sampleFiles: IdentifiedFile[];
}

export interface RecordCollectionCategory extends Identified<UUID> {
    type: string;
    description: string;
}

export interface RecordCollection extends LabeledIdentified<UUID>, Ordered {
    parentId?: UUID;
    department: Department;
    name: string;
    creationEventId: UUID;
    note?: string;
    recordCollectionCategory: RecordCollectionCategory;
    owner: BasicUser;
}

export interface RecordCollectionItem extends Identified<UUID>, Ordered {
    recordCollectionId: UUID
    record: RichDocument; // TODO now true, check in future
}

export interface RecordCollectionItemEntity extends Identified<UUID>, Ordered {
    recordCollectionId: UUID
    recordId: UUID;
}

export interface RecordCollectionInsertRequest {
    record: Record;
    recordCollection: RecordCollection;
}

export interface RecordCollectionBulkInsertRequest {
    records: Record[];
    recordCollection: RecordCollection;
}

export interface ViewableRecordCollection extends RecordCollection {
    deletable: boolean;
    editable: boolean;
    canCreateSubcollection: boolean;
}

export interface RecordCollectionDetail {
    creationEvent: Event;
    lastUpdateEvent: Event;
}
